/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1788825600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Sep 08 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "email",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Sending emails (added / changed / removed)",
          "depth": 0,
          "id": "sending-emails-added--changed--removed"
        }, {
          "value": "Recipient selection",
          "depth": 1,
          "id": "recipient-selection"
        }, {
          "value": "Added, changed, and removed emails",
          "depth": 1,
          "id": "added-changed-and-removed-emails"
        }, {
          "value": "Sending emails (all results)",
          "depth": 0,
          "id": "sending-emails-all-results"
        }, {
          "value": "Recipient selection",
          "depth": 1,
          "id": "recipient-selection-1"
        }, {
          "value": "Subject and body content",
          "depth": 1,
          "id": "subject-and-body-content"
        }, {
          "value": "Templating emails",
          "depth": 0,
          "id": "templating-emails"
        }, {
          "value": "Liquid templating",
          "depth": 1,
          "id": "liquid-templating"
        }, {
          "value": "Table",
          "depth": 0,
          "id": "table"
        }, {
          "value": "Recipient selection",
          "depth": 1,
          "id": "recipient-selection-2"
        }, {
          "value": "CSV",
          "depth": 0,
          "id": "csv"
        }, {
          "value": "JSON",
          "depth": 0,
          "id": "json"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      img: "img",
      ul: "ul",
      a: "a",
      strong: "strong",
      pre: "pre",
      code: "code"
    }, _provideComponents(), props.components), {RowAndColumnOrderingInfo, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the SMTP Email destination, Hightouch can serve as a notifier for various changes. This is a very flexible integration, but here are a few example use cases:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Notify your customer success teams when product usage for a key account suddenly drops"
        }), "\n", _jsx(_components.li, {
          children: "Notify an account executive when product usage surpasses a certain threshold"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary of the number of sign ups, compared to the same day last month"
        }), "\n", _jsx(_components.li, {
          children: "Send a daily summary on the adoption of a core feature"
        }), "\n"]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "generic"
      }), "\n", _jsx(_components.h2, {
        id: "sending-emails-added--changed--removed",
        children: "Sending emails (added / changed / removed)"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch will not send any emails on the initial run to prevent a flood of\nemails to your channel. Emails will start after the this initial sync."
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports sending emails based on rows that have been added, changed, or removed since the last sync run. Each row is sent as an individual email and can be sent to different channels and templated with Liquid."
      }), "\n", _jsx(_components.p, {
        children: "Email formatting is best when the following conditions are met:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "You want to write a query, and have Hightouch detect the added and removed rows"
        }), "\n", _jsx(_components.li, {
          children: "You want a custom email sent per each added and removed rows"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "recipient-selection",
        children: "Recipient selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports both sending emails to specific static recipients, or an email each row. Enable the \"Use column\" toggle to dynamically set the target email addresses.\nHightouch also supports CC and BCC so that multiple recipients can be included on the email chain."
      }), "\n", _jsx(_components.p, {
        children: "The recipient, CC, and BCC sections all support a single email or a comma-separated list of emails"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-email-channel-column.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "added-changed-and-removed-emails",
        children: "Added, changed, and removed emails"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to template the content you would like to send in your email."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "When a row is added in your query results, Hightouch will send the row added content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is changed in your query results, Hightouch will send the row changed content."
        }), "\n", _jsx(_components.li, {
          children: "When a row is deleted in your query results, Hightouch will send the row deleted content."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-email-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "sending-emails-all-results",
        children: "Sending emails (all results)"
      }), "\n", _jsx(_components.p, {
        children: "The batch message mode is similar to the normal message mode, except it sends all rows in the query results rather than only added, changed, and removed rows since the last sync."
      }), "\n", _jsx(_components.p, {
        children: "This mode also allows you to specify how many rows are sent per email, which can range from 1 to 20. With 1 row per email, it would be similar to the message mode, whereas 20 rows per email would be similar to a tabular representation."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch send an email containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added, changed, and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "recipient-selection-1",
        children: "Recipient selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your emails to a static email or list of emails.\nHightouch also supports CC and BCC so that multiple recipients can be included on the email chain."
      }), "\n", _jsx(_components.p, {
        children: "The recipient, CC, and BCC sections all support a single email or a comma-separated list of emails"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-email-channel.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "subject-and-body-content",
        children: "Subject and body content"
      }), "\n", _jsx(_components.p, {
        children: "For each email, Hightouch allows setting the subject and row content. Only the row content is required. The row content is repeated per row in the email and has access to the row values via templating."
      }), "\n", _jsxs(_components.p, {
        children: ["This content can be ", _jsx(_components.a, {
          href: "#liquid-templating",
          children: "templated"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-email-batch-content.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "templating-emails",
        children: "Templating emails"
      }), "\n", _jsx(_components.h3, {
        id: "liquid-templating",
        children: "Liquid templating"
      }), "\n", _jsxs(_components.p, {
        children: ["For both the message and batch message modes, ", _jsx(_components.a, {
          href: "https://shopify.github.io/liquid/",
          children: "Liquid templating "
        }), "is supported. The most important implication of this is that columns from the added or removed row can be accessed as part of the message body. Here is an example message making use of Liquid to insert a column into the message."]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "User with name {{ row[\"name\"] }} and email {{ row[\"email\"] }} has signed up\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "table",
        children: "Table"
      }), "\n", _jsx(_components.p, {
        children: "Table formatting sends all of your query results formatted as a text table. This mode is recommended only for small sets of data."
      }), "\n", _jsx(_components.p, {
        children: "This format is good for the following scenarios."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["You want to write a query, and have Hightouch send an email containing ", _jsx(_components.strong, {
            children: "all"
          }), " rows, formatted as a table"]
        }), "\n", _jsx(_components.li, {
          children: "You don't want Hightouch to detect added and removed rows, but instead want to send all rows on each query run"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "recipient-selection-2",
        children: "Recipient selection"
      }), "\n", _jsx(_components.p, {
        children: "The batch message only supports sending your emails to a static email or list of emails.\nHightouch also supports CC and BCC so that multiple recipients can be included on the email chain."
      }), "\n", _jsx(_components.p, {
        children: "The recipient, CC, and BCC sections all support a single email or a comma-separated list of emails"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-email-channel.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "csv",
        children: "CSV"
      }), "\n", _jsx(_components.p, {
        children: "The CSV option allows you to send a CSV file or a zipped CSV file of all records in the query results for each sync attached to a single email. You can customize the subject and body of the email, as well as the name of the CSV file attachment."
      }), "\n", _jsx(_components.p, {
        children: "This format is useful for reporting purposes, such as sending weekly or monthly reports."
      }), "\n", _jsx(_components.p, {
        children: "If sending a zipped CSV file, make sure that your email service has the appropriate permissions to send .zip files to recipients. This may require unblocking IP addresses that your service uses."
      }), "\n", _jsx(_components.h2, {
        id: "json",
        children: "JSON"
      }), "\n", _jsx(_components.p, {
        children: "The JSON option works the same way as the CSV option, but attaches all records in the query results as a JSON file instead. You can customize the subject and body of the email, as well as the name of the JSON file attachment."
      }), "\n", _jsx(_components.p, {
        children: "Unlike the CSV option, JSON attachments don't support zip compression."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
